import { Attribute, Component, HostBinding, Input } from '@angular/core';

import { FORM_CONTROL_RADIUS_CLASS } from '@shared/styles/form-control-shell';
import { ComponentsSizeEnum } from '@shared/types/enums/components-size.enum';
import { StringHelper } from '../../../helpers/string.helper';
import { ButtonContentEnum } from './types/button-content.enum';
import { ButtonTypeEnum } from './types/button-type.enum';

const BUTTON_SIZE_CLASSES: Record<ComponentsSizeEnum, string> = {
  [ComponentsSizeEnum.SMALL]: 'box-border min-h-8 h-8 px-2.5 text-xs leading-normal',
  [ComponentsSizeEnum.MEDIUM]: 'box-border min-h-9 h-9 px-3 text-sm leading-normal',
  [ComponentsSizeEnum.LARGE]: 'box-border min-h-10 h-10 px-4 text-base leading-normal'
};

const BUTTON_ICON_SIZE_CLASSES: Record<ComponentsSizeEnum, string> = {
  [ComponentsSizeEnum.SMALL]: 'box-border min-h-8 h-8 w-8 min-w-8 max-w-8 p-0 text-sm leading-none',
  [ComponentsSizeEnum.MEDIUM]: 'box-border min-h-9 h-9 w-9 min-w-9 max-w-9 p-0 text-base leading-none',
  [ComponentsSizeEnum.LARGE]: 'box-border min-h-10 h-10 w-10 min-w-10 max-w-10 p-0 text-lg leading-none'
};

const BUTTON_COLORS: Record<ButtonTypeEnum, string> = {
  [ButtonTypeEnum.BASIC]: 'border border-transparent bg-basic-500 hover:opacity-80 focus:ring-inset focus:ring-basic-500/50 text-text-100',
  [ButtonTypeEnum.INFO]: 'border border-transparent bg-info-500 hover:bg-info-500/90 focus:ring-inset focus:ring-info-500/50 text-text-100',
  [ButtonTypeEnum.SUCCESS]: 'border border-transparent bg-success-500 hover:bg-success-500/90 focus:ring-inset focus:ring-success-500/50 text-text-100',
  [ButtonTypeEnum.WARNING]: 'border border-transparent bg-warning-500 hover:bg-warning-500/90 focus:ring-inset focus:ring-warning-500/50 text-text-100',
  [ButtonTypeEnum.DANGER]: 'border border-transparent bg-danger-500 hover:bg-danger-500/90 focus:ring-inset focus:ring-danger-500/50 text-text-100',
  [ButtonTypeEnum.PRIMARY]: 'border border-transparent bg-primary-500 hover:bg-primary-500/90 focus:ring-inset focus:ring-primary-500/50 text-basic-1200',
  [ButtonTypeEnum.LIGHT]: 'border border-transparent bg-basic-100 hover:bg-basic-100/90 focus:ring-inset focus:ring-basic-100/50 text-basic-100'
};

const BUTTON_COLORS_OUTLINE: Record<ButtonTypeEnum, string> = {
  [ButtonTypeEnum.BASIC]: 'border border-primary-100 bg-basic-500/10 hover:bg-basic-500/15 focus:ring-inset focus:ring-basic-500/50 text-basic-500',
  [ButtonTypeEnum.INFO]: 'border !border-info-500 bg-info-500/10 hover:bg-info-500/15 focus:ring-inset focus:ring-info-500/50 text-info-500',
  [ButtonTypeEnum.SUCCESS]: 'border !border-success-500 bg-success-500/10 hover:bg-success-500/15 focus:ring-inset focus:ring-success-500/50 text-success-500',
  [ButtonTypeEnum.WARNING]: 'border !border-warning-500 bg-warning-500/10 hover:bg-warning-500/15 focus:ring-inset focus:ring-warning-500/50 text-warning-500',
  [ButtonTypeEnum.DANGER]: 'border !border-danger-500 bg-danger-500/10 hover:bg-danger-500/15 focus:ring-inset focus:ring-danger-500/50 text-danger-500',
  [ButtonTypeEnum.PRIMARY]: 'border !border-primary-500 bg-primary-500/10 hover:bg-primary-500/20 focus:ring-inset focus:ring-primary-500/50 text-primary-500',
  [ButtonTypeEnum.LIGHT]: 'border !border-primary-100 bg-basic-100/1 hover:bg-basic-100/15 focus:ring-inset focus:ring-basic-100/50 text-basic-100'
};

const BUTTON_SHELL = 'no-underline inline-flex items-center justify-center focus:outline-none focus:ring cursor-pointer transition-colors duration-150';

@Component({
  selector: '[lButton]',
  template: '<ng-content></ng-content>'
})
export class ButtonComponent {
  protected readonly staticHostClass: string;

  public constructor(@Attribute('class') hostClassAttr: string | null) {
    const raw = hostClassAttr?.trim() ?? '';
    this.staticHostClass = raw.replace(/\s+/g, ' ').trim();
  }

  @HostBinding('class')
  public get class() {
    return this.generateClasses;
  }

  @Input({ alias: 'bType' })
  public set type(content: ButtonTypeEnum | string) {
    this._type = StringHelper.stringToEnum(ButtonTypeEnum, content) || ButtonTypeEnum.BASIC;
  }

  @Input({ alias: 'bSize' })
  public set size(content: ComponentsSizeEnum | string) {
    this._size = StringHelper.stringToEnum(ComponentsSizeEnum, content) ?? ComponentsSizeEnum.MEDIUM;
  }

  @Input({ alias: 'outline' })
  public set outline(content: boolean | string) {
    if (typeof content === 'string' && content === '') {
      this._outline = true;
      return;
    }

    this._outline = !!content;
  }

  @Input({ alias: 'bContent' })
  public set content(mode: ButtonContentEnum | string | undefined) {
    if (mode == null || (typeof mode === 'string' && mode.trim() === '')) {
      this._content = ButtonContentEnum.LABEL;
      return;
    }

    this._content = StringHelper.stringToEnum(ButtonContentEnum, String(mode)) ?? ButtonContentEnum.LABEL;
  }

  private _type: ButtonTypeEnum = ButtonTypeEnum.BASIC;

  private _size: ComponentsSizeEnum = ComponentsSizeEnum.MEDIUM;

  private _outline = false;

  private _content: ButtonContentEnum = ButtonContentEnum.LABEL;

  private get generateClasses(): string {
    const colorClasses = this._outline ? BUTTON_COLORS_OUTLINE[this._type] : BUTTON_COLORS[this._type];
    const isIcon = this._content !== ButtonContentEnum.LABEL;
    const sizeClasses = isIcon ? BUTTON_ICON_SIZE_CLASSES[this._size] : BUTTON_SIZE_CLASSES[this._size];
    const shapeClass = FORM_CONTROL_RADIUS_CLASS;
    const layoutClasses = isIcon ? `${BUTTON_SHELL} ${shapeClass} text-center shrink-0` : `${BUTTON_SHELL} ${shapeClass} text-center shrink-0 text-nowrap`;

    return [colorClasses, sizeClasses, layoutClasses, this.staticHostClass].filter(Boolean).join(' ');
  }
}
